import { useRef } from "react";
import { FileText, X, Paperclip, Layers } from "lucide-react";
import { api } from "@/lib/api";
import { Label } from "@/components/ui/label";

export const MAX_PDF = 10 * 1024 * 1024;
export const MAX_FILING = 50 * 1024 * 1024;
const mb = (n) => `${(n / 1048576).toFixed(1)} MB`;

/** Validates a candidate list client-side; returns an error string or null. */
export const checkPdfs = (files) => {
  for (const f of files) {
    if (f.type !== "application/pdf" && !f.name.toLowerCase().endsWith(".pdf")) return `${f.name}: only PDF files can be attached`;
    if (f.size > MAX_PDF) return `${f.name} is ${mb(f.size)} — the limit is 10 MB per PDF`;
  }
  const total = files.reduce((s, f) => s + f.size, 0);
  if (total > MAX_FILING) return `Total ${mb(total)} exceeds the 50 MB limit per filing`;
  return null;
};

/** Per-file progress derived from the cumulative bytes of one multipart request (files are sent in order). */
const perFile = (files, loaded) => {
  let acc = 0;
  return files.map((f) => {
    const start = acc; acc += f.size;
    if (loaded >= acc) return 100;
    if (loaded <= start) return 0;
    return Math.round(((loaded - start) / f.size) * 100);
  });
};

/** Uploads the selected PDFs as one filing; resolves to the created document rows. */
export const uploadFiling = async (caseId, title, category, files, { merge = false, onProgress } = {}) => {
  if (!files?.length) return [];
  const fd = new FormData();
  fd.append("case_id", caseId);
  fd.append("title", title);
  fd.append("category", category);
  fd.append("merge", merge ? "true" : "false");
  files.forEach((f) => fd.append("files", f));
  const { data } = await api.post("/documents/batch", fd, {
    headers: { "Content-Type": "multipart/form-data" },
    onUploadProgress: (ev) => {
      if (!onProgress) return;
      const payload = ev.total || files.reduce((s, f) => s + f.size, 0);
      const ratio = Math.min(1, ev.loaded / payload);
      onProgress({ overall: Math.round(ratio * 100), files: perFile(files, ratio * files.reduce((s, f) => s + f.size, 0)) });
    },
  });
  onProgress?.({ overall: 100, files: files.map(() => 100), done: true });
  return data;
};

const Bar = ({ value, testId }) => (
  <div className="h-1.5 w-28 bg-muted overflow-hidden" data-testid={testId} aria-valuenow={value}>
    <div className="h-full bg-blue-900 transition-[width] duration-200" style={{ width: `${value}%` }} />
  </div>
);

/** Multi-PDF picker with per-file size feedback, per-file upload progress and a merge option. */
export const MultiPdfPicker = ({ files, onChange, label = "Attach scanned PDFs", testId = "pdf-picker", required = false,
  progress = null, merge = false, onMergeChange = null }) => {
  const input = useRef(null);
  const total = files.reduce((s, f) => s + f.size, 0);
  const error = files.length ? checkPdfs(files) : null;
  const uploading = progress && !progress.done;
  const add = (list) => {
    const next = [...files];
    for (const f of Array.from(list || [])) if (!next.some((x) => x.name === f.name && x.size === f.size)) next.push(f);
    onChange(next);
    if (input.current) input.current.value = "";
  };
  return (
    <div className="space-y-2" data-testid={testId}>
      <Label className="label-caps flex items-center gap-2"><Paperclip className="h-3.5 w-3.5" /> {label}{required ? " *" : ""}</Label>
      <input ref={input} type="file" accept="application/pdf" multiple data-testid={`${testId}-input`} disabled={uploading}
        onChange={(e) => add(e.target.files)}
        className="block w-full text-sm file:mr-3 file:h-10 file:px-4 file:border-0 file:bg-slate-900 file:text-white file:uppercase file:text-xs file:tracking-widest file:cursor-pointer" />
      <p className="text-[11px] text-muted-foreground">Select several PDFs at once · 10 MB per file · 50 MB per filing{files.length ? ` · ${files.length} file(s), ${mb(total)}` : ""}</p>
      {files.length > 0 && (
        <ul className="border border-border divide-y divide-border" data-testid={`${testId}-list`}>
          {files.map((f, i) => {
            const pct = progress?.files?.[i] ?? null;
            return (
              <li key={`${f.name}-${f.size}`} className={`px-3 py-2 flex items-center gap-3 text-sm ${f.size > MAX_PDF ? "bg-red-50 dark:bg-red-950/30" : ""}`} data-testid={`${testId}-file-${i}`}>
                <FileText className="h-4 w-4 text-muted-foreground shrink-0" />
                <span className="truncate flex-1">{f.name}</span>
                {pct !== null && <><Bar value={pct} testId={`${testId}-progress-${i}`} /><span className="text-xs font-mono w-10 text-right" data-testid={`${testId}-pct-${i}`}>{pct}%</span></>}
                <span className="text-xs text-muted-foreground font-mono">{mb(f.size)}</span>
                {!uploading && (
                  <button type="button" onClick={() => onChange(files.filter((_, j) => j !== i))} aria-label="Remove"
                    data-testid={`${testId}-remove-${i}`} className="text-red-800 hover:text-red-600 transition-colors duration-200"><X className="h-4 w-4" /></button>
                )}
              </li>
            );
          })}
        </ul>
      )}
      {files.length > 1 && onMergeChange && (
        <label className="flex items-center gap-2 text-sm cursor-pointer" data-testid={`${testId}-merge-label`}>
          <input type="checkbox" checked={merge} disabled={uploading} onChange={(e) => onMergeChange(e.target.checked)} className="h-4 w-4 accent-blue-900" data-testid={`${testId}-merge`} />
          <Layers className="h-3.5 w-3.5 text-muted-foreground" /> Combine into a single indexed, paginated PDF bundle for the tribunal
        </label>
      )}
      {progress && (
        <p className="text-xs text-muted-foreground" data-testid={`${testId}-overall`}>
          {progress.done ? "Upload complete" : `Uploading… ${progress.overall}%`}
        </p>
      )}
      {error && <p className="text-xs text-red-800 dark:text-red-400" data-testid={`${testId}-error`}>{error}</p>}
    </div>
  );
};
